import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { ReactNode } from "react";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import {
  ACTIVE_REFERRAL_STATUSES,
  REFERRAL_STATUS,
} from "@/referrals/constants";
import { IReferral } from "@/referrals/referral";

interface ReferralOptionsDropdownProps {
  referral: IReferral;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  onDelete?: () => void;
  children?: ReactNode;
}

export const ReferralOptionsDropdown = ({
  referral,
  onLost,
  onMoveToPaid,
  onDelete,
  children,
}: ReferralOptionsDropdownProps) => {
  const hasLostOption =
    !!onLost &&
    ACTIVE_REFERRAL_STATUSES.filter(
      (status) => status !== REFERRAL_STATUS.CLOSED,
    ).includes(referral.status);

  const hasMoveToPaidOption =
    referral.payouts.length > 0 &&
    !!onMoveToPaid &&
    referral.status !== REFERRAL_STATUS.PAID;

  const hasDropdownOptions = hasLostOption || hasMoveToPaidOption || !!onDelete;

  if (!hasDropdownOptions) {
    return null;
  }

  const handleAction =
    (action: "lost" | "paid" | "delete") => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      switch (action) {
        case "lost":
          onLost?.();
          break;
        case "paid":
          onMoveToPaid?.();
          break;
        case "delete":
          onDelete?.();
          break;
      }
    };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {children || (
          <Button
            variant="icon"
            size="icon"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <EllipsisVerticalIcon />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {hasLostOption && (
          <DropdownMenuItem onClick={handleAction("lost")}>
            Mark as Lost
          </DropdownMenuItem>
        )}
        {hasMoveToPaidOption && (
          <DropdownMenuItem onClick={handleAction("paid")}>
            Move to Paid
          </DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem onClick={handleAction("delete")}>
            Delete Referral
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
